import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin, ArrowUpRight, Quote, MessageSquare, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SITE } from "../lib/site";
import { MaskedLine, MaskedLineOnView, Reveal } from "../components/Reveal";
import BrandMarquee from "../components/BrandMarquee";
import SectionCTA from "../components/SectionCTA";
import ServiceIcon from "../components/ServiceIcon";
import GoogleReviewCard, { GoogleReviewsBadge } from "../components/GoogleReviewCard";
import TrustBadges from "../components/TrustBadges";
import TrustStrip from "../components/TrustStrip";
import WhatToBring from "../components/WhatToBring";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A192F]" data-testid="hero">
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
          <img
            src={SITE.images.hero}
            alt="Pennsylvania license plate on a car"
            className="w-full h-[115%] object-cover"
            loading="eager"
          />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#0A192F] z-[1]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/50 via-[#0A192F]/50 to-[#0A192F]/95 z-[2]" />
        {/* Radial vignette right side for text contrast */}
        <div className="absolute inset-0 z-[2] bg-[radial-gradient(ellipse_at_left,_rgba(10,25,47,0.85),_transparent_60%)]" />

        <div className="relative z-10 min-h-[100svh] max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 flex flex-col justify-end pb-14 sm:pb-20 lg:pb-28 pt-32 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#C5A059]"
          >
            <span className="w-8 h-px bg-[#C5A059]" />
            Rhawn Auto Tag · Northeast Philadelphia
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mt-5 md:mt-6 font-serif italic text-lg sm:text-xl md:text-2xl text-[#C5A059] leading-snug"
          >
            Philadelphia's Trusted Vehicle Paperwork Provider.
          </motion.p>

          <h1 className="font-serif mt-4 md:mt-5 text-[10vw] sm:text-[7.5vw] md:text-[5.8vw] lg:text-[4.6vw] leading-[0.98] tracking-tight max-w-[22ch] text-white">
            <MaskedLine delay={0.45}>Vehicle Paperwork,</MaskedLine>
            <MaskedLine delay={0.6}>
              Notary <span className="italic text-[#C5A059]">Assistance</span> &amp; Auto Insurance.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-6 md:mt-8 max-w-xl text-white/80 text-base md:text-lg font-light leading-relaxed"
          >
            Everything you need for your vehicle — under one roof in
            Northeast Philadelphia. Fast, friendly, hassle-free service.
            Walk-ins welcome. No appointment necessary.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
          >
            <a href={SITE.phoneHref} className="btn-gold justify-center" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" /> Call Now
            </a>
            <a
              href={SITE.mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost justify-center text-white border-white/60"
              data-testid="hero-directions-btn"
            >
              <MapPin className="w-4 h-4" /> Get Directions
            </a>
            <Link
              to="/insurance"
              className="btn-ghost justify-center text-white border-white/60"
              data-testid="hero-quote-btn"
            >
              Get Insurance Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Trust badges beneath hero CTAs */}
          <div className="mt-8 md:mt-10 max-w-4xl">
            <TrustBadges variant="hero" />
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="hidden md:flex mt-16 justify-between items-end gap-8 text-xs text-white/60"
          >
            <div>
              <div className="label-mini text-white/45">Open Today</div>
              <div className="mt-2 text-white/90 font-mono-tab">Mon – Sat: 9:00 AM – 9:00 PM</div>
              <div className="mt-1 text-white/70 font-mono-tab text-[11px]">Sun: 12:00 PM – 6:00 PM</div>
            </div>
            <div>
              <div className="label-mini text-white/45">Address</div>
              <div className="mt-2 text-white/90">{SITE.addressShort}</div>
            </div>
            <div>
              <div className="label-mini text-white/45">Direct Line</div>
              <div className="mt-2 text-white/90 font-mono-tab whitespace-nowrap">{SITE.phone}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <BrandMarquee
        items={[
          "Title Paperwork",
          "Tag Paperwork Help",
          "Notary Services",
          "Auto Insurance",
          "Plate Applications",
          "Renewal Assistance",
          "Custom Plate Apps",
          "Duplicate Title Help",
        ]}
      />

      {/* TRUST STRIP — credentials + payments + security */}
      <TrustStrip />

      {/* INTRO / CHAPTER 01 — APPROACH */}
      <ApproachSection />

      {/* SERVICES — Interactive cards with icons */}
      <ServicesShowcase />

      {/* CTA after services */}
      <SectionCTA
        eyebrow="Everything in one visit"
        title={<>Bring the paperwork. <span className="italic text-[#C5A059]">We handle the rest.</span></>}
        subtitle="Call ahead so we can prep your file — most customers are in and out inside thirty minutes."
        secondaryLabel="See All Services"
        secondaryTo="/services"
        testid="cta-after-services"
      />

      {/* CHAPTER 02 - IMAGE COLLAGE */}
      <ImageCollage />

      {/* WHY CHOOSE US */}
      <WhyUs />

      {/* CTA after why */}
      <SectionCTA
        variant="dark"
        eyebrow="Ready when you are"
        title={<>Call us <span className="italic text-[#C5A059]">now</span>.</>}
        subtitle="Weekend hours. Multilingual staff (English / Spanish / Portuguese / Arabic). Free parking. Walk-ins welcome."
        secondaryLabel="Text Us"
        secondaryTo="/contact"
        testid="cta-after-why"
      />

      {/* GOOGLE-STYLE TESTIMONIALS — hidden until real Google Reviews sync is set up */}
      {/* <Testimonials /> */}

      {/* WHAT TO BRING */}
      <WhatToBring />

      {/* FAQ */}
      <section className="relative py-24 md:py-32 lg:py-40 bg-[#FBF9F5] overflow-hidden" data-testid="faq-section">
        {/* Decorative gold hairline pattern */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden>
          <div className="absolute top-1/4 -left-24 w-96 h-96 rounded-full border-[40px] border-[#C5A059]" />
          <div className="absolute bottom-1/4 -right-24 w-72 h-72 rounded-full border-[30px] border-[#0A192F]" />
        </div>

        <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
          {/* Heading row */}
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14 md:mb-16">
            <div className="lg:col-span-7">
              <div className="section-divider max-w-lg mb-8">
                <span>05 · Questions</span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[#0A192F]">
                Everything you were <br className="hidden md:block" />about to <span className="italic text-[#C5A059]">ask.</span>
              </h2>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <p className="text-[#0A192F]/70 leading-relaxed">
                Answers to the most common questions from Philadelphia drivers.
                Not seeing yours? Call the counter — a real person picks up.
              </p>
              <a
                href={SITE.phoneHref}
                className="btn-gold mt-6 text-sm"
                data-testid="faq-call"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </div>

          {/* FAQ card grid */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-5">
            {SITE.faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <FaqCard f={f} i={i} />
              </Reveal>
            ))}
            {/* Bonus contact card */}
            <Reveal delay={SITE.faqs.length * 0.06}>
              <div
                className="relative overflow-hidden border border-[#C5A059]/40 bg-[#0A192F] text-white p-7 md:p-8 h-full flex flex-col justify-between md:col-span-2 md:min-h-[220px]"
                data-testid="faq-contact-card"
              >
                <div className="relative z-10">
                  <div className="label-mini text-white/50">Still have questions?</div>
                  <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">
                    Call the counter. <span className="italic text-[#C5A059]">A real person picks up.</span>
                  </h3>
                  <p className="mt-3 text-white/70 max-w-md text-sm md:text-base">
                    Multilingual staff · Mon–Sat 9 AM–9 PM · Sunday 12 PM–6 PM.
                  </p>
                </div>
                <div className="relative z-10 mt-6 flex flex-wrap gap-3">
                  <a href={SITE.phoneHref} className="btn-gold text-sm" data-testid="faq-card-call">
                    <Phone className="w-4 h-4" /> Call {SITE.phone}
                  </a>
                  <a
                    href={SITE.smsHref}
                    className="btn-ghost text-sm text-white border-white/50"
                    data-testid="faq-card-text"
                  >
                    <MessageSquare className="w-4 h-4" /> Text Us
                  </a>
                </div>
                {/* Decorative gold circles */}
                <span className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full border-[24px] border-[#C5A059]/25" aria-hidden />
                <span className="pointer-events-none absolute -bottom-24 -right-8 w-64 h-64 rounded-full border-[16px] border-white/5" aria-hidden />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAP + HOURS */}
      <section className="py-20 md:py-24 bg-[#0A192F] text-white" data-testid="map-section">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-mini text-white/50">Come see us</div>
            <h2 className="font-serif mt-4 text-4xl sm:text-5xl leading-tight">
              Corner of Loney &amp; Bustleton.
            </h2>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Free parking in front. Multilingual staff (English / Spanish / Portuguese / Arabic).
              Walk-ins welcome all week — evenings and Sundays too.
            </p>

            <div className="mt-8 md:mt-10 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="label-mini text-white/45">Address</div>
                <div className="mt-2 text-white/90">{SITE.address}</div>
              </div>
              <div>
                <div className="label-mini text-white/45">Phone</div>
                <a href={SITE.phoneHref} className="mt-2 block text-[#C5A059] font-mono-tab whitespace-nowrap">
                  {SITE.phone}
                </a>
              </div>
              <div className="col-span-2">
                <div className="label-mini text-white/45">Hours</div>
                <ul className="mt-2 space-y-1 font-mono-tab">
                  {SITE.hours.map((h) => (
                    <li key={h.day} className="flex justify-between max-w-xs">
                      <span>{h.day}</span>
                      <span className="text-[#C5A059]">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={SITE.phoneHref} className="btn-gold text-sm justify-center" data-testid="map-call">
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
              <a
                href={SITE.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost text-sm text-white border-white/50 justify-center"
                data-testid="map-directions"
              >
                <MapPin className="w-4 h-4" /> Directions
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-sm overflow-hidden aspect-[16/12] lg:aspect-[4/3] border border-white/10">
              <iframe
                title="Map to Rhawn Auto Tags"
                src={SITE.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
                loading="lazy"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA STRIP */}
      <SectionCTA
        title={<>Ready when <span className="italic text-[#C5A059]">you are.</span></>}
        subtitle="Vehicle paperwork made simple — call us or walk in seven days a week. Free parking, multilingual staff, no appointment needed."
        secondaryLabel="Text Us"
        secondaryTo="/contact"
        testid="cta-final"
      />
    </>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#0A192F]">{n}</div>
      <div className="mt-2 text-[#0A192F]/60 text-xs sm:text-sm">{label}</div>
    </div>
  );
}

function ServicesShowcase() {
  const featured = SITE.services.slice(0, 8);
  return (
    <section className="py-24 md:py-32 lg:py-44 bg-white" data-testid="services-showcase">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex justify-between items-end flex-wrap gap-6">
          <div>
            <div className="label-mini">02 · Services</div>
            <h2 className="font-serif mt-5 md:mt-6 text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-[#0A192F] max-w-2xl">
              One counter. <span className="italic">Eleven</span> services.
            </h2>
          </div>
          <Link to="/services" className="link-under text-sm text-[#0A192F]/70" data-testid="see-all-services">
            See all services →
          </Link>
        </div>

        <div className="mt-14 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {featured.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.04}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.key}`}
                className="group block relative border border-[#0A192F]/10 bg-white hover:bg-[#0A192F] hover:border-[#C5A059]/60 hover:shadow-2xl hover:shadow-[#0A192F]/10 hover:-translate-y-1 transition-all duration-500 ease-out p-7 md:p-8 h-full overflow-hidden"
                data-cursor="hover"
                aria-label={`${s.name} — ${s.blurb}`}
              >
                <span className="absolute top-0 right-0 w-12 h-12 border-t border-r border-[#C5A059]/0 group-hover:border-[#C5A059] transition-colors duration-500" />

                <div className="flex items-center justify-between">
                  <span
                    className="w-16 h-16 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                    style={{ background: s.color, color: s.color === "#0A192F" ? "#C5A059" : "#0A192F" }}
                  >
                    <ServiceIcon name={s.icon} className="w-7 h-7" />
                  </span>
                  <span className="text-[#C5A059] font-mono-tab text-xs">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="mt-8 font-serif text-xl md:text-[23px] text-[#0A192F] group-hover:text-white transition-colors duration-500 leading-tight">
                  {s.name}
                </h3>
                <p className="mt-3 text-[#0A192F]/65 group-hover:text-white/75 text-sm leading-relaxed transition-colors duration-500">
                  {s.blurb}
                </p>

                <div className="mt-7 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0A192F]/60 group-hover:text-[#C5A059] transition-colors duration-500">
                  Learn More
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-500" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImageCollage() {
  return (
    <section className="py-20 md:py-28 bg-[#0A192F] text-white overflow-hidden" data-testid="image-collage">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
        {/* Left — Editorial statement */}
        <div className="lg:col-span-4">
          <div className="label-mini text-white/50">Every service</div>
          <h3 className="font-serif mt-5 text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            A Philadelphia counter for <span className="italic text-[#C5A059]">everything</span> vehicle-related.
          </h3>
          <p className="mt-6 text-white/70 leading-relaxed max-w-md text-sm md:text-base">
            Titles, tags, notary and auto insurance — same-day, at one convenient
            Northeast Philadelphia address. Real specialists, honest pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Same-Day Help", "Paperwork Prep", "Multilingual", "Free Parking"].map((t) => (
              <span key={t} className="inline-flex items-center px-3 py-1.5 border border-white/20 rounded-full text-[10px] tracking-wide text-white/70">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Right — Layered image grid */}
        <div className="lg:col-span-8 grid grid-cols-6 gap-3 md:gap-4 h-[520px] md:h-[560px] relative">
          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="col-span-4 row-span-2 relative overflow-hidden group"
          >
            <img
              src={SITE.images.notary}
              alt="Notary Services signing a document"
              className="w-full h-full object-cover clip-portrait rounded-tr-3xl group-hover:scale-105 transition-transform duration-1000 ease-out"
              loading="lazy"
            />
            <figcaption className="absolute bottom-4 left-4 flex items-center gap-2 bg-[#0A192F]/80 backdrop-blur-sm border border-white/20 px-3 py-2 rounded-full">
              <ServiceIcon name="Stamp" className="w-3.5 h-3.5 text-[#C5A059]" />
              <span className="text-[11px] text-white/90">Certified Notary</span>
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.15, duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="col-span-2 relative overflow-hidden group"
          >
            <img
              src={SITE.images.plate}
              alt="Pennsylvania license plate close-up"
              className="w-full h-full object-cover clip-portrait group-hover:scale-105 transition-transform duration-1000 ease-out"
              loading="lazy"
            />
            <figcaption className="absolute bottom-3 left-3 flex items-center gap-2 bg-[#0A192F]/80 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 rounded-full">
              <ServiceIcon name="RectangleHorizontal" className="w-3 h-3 text-[#C5A059]" />
              <span className="text-[10px] text-white/90">PA Plates</span>
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.3, duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="col-span-2 relative overflow-hidden group"
          >
            <img
              src={SITE.images.insurancePaperwork}
              alt="Auto insurance documents and vehicle keys"
              className="w-full h-full object-cover clip-portrait rounded-bl-3xl group-hover:scale-105 transition-transform duration-1000 ease-out"
              loading="lazy"
            />
            <figcaption className="absolute bottom-3 left-3 flex items-center gap-2 bg-[#0A192F]/80 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 rounded-full">
              <ServiceIcon name="ShieldCheck" className="w-3 h-3 text-[#C5A059]" />
              <span className="text-[10px] text-white/90">Insurance</span>
            </figcaption>
          </motion.figure>

          {/* Gold corner accent */}
          <span className="pointer-events-none absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-[#C5A059]/60" aria-hidden />
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const items = [
    { t: "Experienced Paperwork Team", d: "A trusted Philadelphia service provider — we prepare, review, and organize your paperwork accurately the first time.", icon: "ShieldCheck" },
    { t: "Honest Pricing", d: "PennDOT fees shown line-by-line. Our service fees are transparent, itemized, and never inflated.", icon: "Receipt" },
    { t: "Free Parking · Walk-Ins Welcome", d: "Pull up out front. No appointment necessary. Walk-in service every day we're open.", icon: "ParkingCircle" },
    { t: "English, Spanish, Portuguese & Arabic", d: "Multilingual staff — comfortable paperwork help in English, Spanish, Portuguese and Arabic.", icon: "Languages" },
    { t: "Fast, Friendly Service", d: "Real people who answer the phone, text back, and remember your name. Northeast Philly since day one.", icon: "HandHeart" },
  ];
  return (
    <section className="py-24 md:py-32 lg:py-40 bg-white overflow-hidden" data-testid="why-us">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {/* LEFT — Sticky heading + image collage */}
        <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
          <div className="label-mini">03 · Why Rhawn</div>
          <h2 className="font-serif mt-5 md:mt-6 text-4xl sm:text-5xl md:text-6xl leading-[1.02] text-[#0A192F]">
            Why Rhawn Auto Tag <span className="italic text-[#C5A059]">&amp; Notary?</span>
          </h2>
          <p className="mt-6 text-[#0A192F]/70 max-w-md leading-relaxed">
            Northeast Philadelphia's most trusted vehicle paperwork provider —
            built on transparent pricing, real conversations, and doing the
            paperwork right the first time.
          </p>

          {/* Layered image collage */}
          <div className="mt-10 relative h-[380px] max-w-[440px] hidden md:block">
            <img
              src={SITE.images.office}
              alt="Rhawn Auto Tag Notary — Philadelphia office"
              className="absolute top-0 left-0 w-[62%] h-[300px] object-cover rounded-tr-3xl clip-portrait"
              loading="lazy"
            />
            <img
              src={SITE.images.plate}
              alt="Pennsylvania license plate detail"
              className="absolute bottom-0 right-0 w-[52%] h-[220px] object-cover rounded-bl-3xl clip-portrait border-4 border-white shadow-xl shadow-[#0A192F]/10"
              loading="lazy"
            />
            <span className="pointer-events-none absolute -top-3 -right-3 w-20 h-20 border-t-2 border-r-2 border-[#C5A059]/60" aria-hidden />
          </div>
        </div>

        {/* RIGHT — Items with icons */}
        <div className="lg:col-span-7 lg:col-start-6 space-y-4 md:space-y-5">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.06}>
              <motion.div
                whileHover={{ x: 6 }}
                transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
                className="group flex gap-5 md:gap-7 border border-[#0A192F]/10 hover:border-[#C5A059]/50 hover:shadow-lg hover:shadow-[#0A192F]/5 bg-white p-6 md:p-7 transition-all duration-500 relative overflow-hidden"
              >
                {/* Big number watermark */}
                <span
                  className="chapter-num absolute -right-4 -bottom-8 text-[7rem] md:text-[8rem] leading-none opacity-40 group-hover:opacity-100 group-hover:text-[#C5A059]/25 transition-all duration-700 pointer-events-none select-none"
                  aria-hidden
                >
                  {String(i + 1).padStart(2, "0")}
                </span>

                <span className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-full bg-[#0A192F] text-[#C5A059] flex items-center justify-center group-hover:bg-[#C5A059] group-hover:text-[#0A192F] transition-colors duration-500">
                  <ServiceIcon name={it.icon} className="w-6 h-6" />
                </span>

                <div className="flex-1 min-w-0 relative">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">
                    <span className="w-6 h-px bg-[#C5A059]" /> Reason {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 font-serif text-xl sm:text-2xl md:text-[27px] text-[#0A192F] leading-tight">
                    {it.t}
                  </h3>
                  <p className="mt-2 text-[#0A192F]/65 text-sm md:text-base leading-relaxed max-w-xl">
                    {it.d}
                  </p>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-24 md:py-32 bg-[#0A192F] text-white" data-testid="testimonials">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex justify-between items-end flex-wrap gap-6">
          <div>
            <div className="label-mini text-white/50">Voices · Google Reviews</div>
            <h2 className="font-serif mt-5 md:mt-6 text-4xl sm:text-5xl md:text-6xl leading-[1.02]">
              Trusted by drivers across <span className="italic text-[#C5A059]">Northeast Philadelphia.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/70">
            <GoogleReviewsBadge dark />
          </div>
        </div>
        <div className="mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {SITE.testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <GoogleReviewCard {...t} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


function ApproachSection() {
  const steps = [
    { icon: "DoorOpen", t: "Walk in", d: "No appointment needed. Free parking on Bustleton Ave." },
    { icon: "ClipboardCheck", t: "We verify", d: "Our team prepares & double-checks every form at the counter." },
    { icon: "Send", t: "Paperwork prepared", d: "Documents organized and reviewed while you wait." },
    { icon: "CarFront", t: "Ready to submit", d: "Notarized documents and paperwork in hand — same visit." },
  ];

  return (
    <section
      className="relative py-24 md:py-32 lg:py-40 bg-[#FBF9F5] overflow-hidden"
      data-testid="chapter-1"
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">

        {/* LEFT — Visual composition */}
        <div className="lg:col-span-5 relative">
          <div className="label-mini">01 · Approach</div>
          <h2 className="font-serif mt-5 text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[#0A192F]">
            <MaskedLineOnView delay={0.05}>Philadelphia's</MaskedLineOnView>
            <MaskedLineOnView delay={0.15}>vehicle paperwork</MaskedLineOnView>
            <MaskedLineOnView delay={0.25}>
              <span className="italic text-[#C5A059]">assistance</span> team.
            </MaskedLineOnView>
          </h2>

          <Reveal delay={0.3}>
            <p className="mt-8 text-[#0A192F]/70 text-base md:text-lg leading-relaxed max-w-md">
              Rhawn Auto Tag helps drivers in Northeast Philadelphia
              organize and prepare their vehicle paperwork, plus in-house
              notary and auto insurance — all under one roof.
            </p>
          </Reveal>

          {/* Image + floating credential card */}
          <Reveal delay={0.35}>
            <div className="mt-12 relative aspect-[4/5] max-w-[440px]">
              <img
                src={SITE.images.handshake}
                alt="Customer at the counter — Rhawn Auto Tag Philadelphia"
                className="w-full h-full object-cover rounded-tr-3xl clip-portrait"
                loading="lazy"
              />
              {/* Gold hairline border accent */}
              <span className="pointer-events-none absolute -bottom-3 -right-3 w-24 h-24 border-r-2 border-b-2 border-[#C5A059]/60" aria-hidden />

              {/* Floating credential card */}
              <div className="absolute -bottom-6 left-6 md:-left-8 bg-white border border-[#0A192F]/10 shadow-xl shadow-[#0A192F]/10 p-5 md:p-6 max-w-[240px]">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#0A192F] text-[#C5A059] flex items-center justify-center">
                    <ServiceIcon name="ShieldCheck" className="w-5 h-5" />
                  </span>
                  <div className="leading-tight">
                    <div className="label-mini text-[#0A192F]/60">Credential</div>
                    <div className="font-serif text-[15px] text-[#0A192F] mt-0.5">
                      Trusted Provider
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-[11px] text-[#0A192F]/60 leading-relaxed">
                  Licensed &amp; insured Philadelphia agent — every filing handled correctly, the first time.
                </p>
              </div>

              {/* Watermark 01 behind everything */}
              <span
                className="chapter-num absolute -top-16 -right-6 text-[16rem] md:text-[18rem] leading-none select-none pointer-events-none"
                aria-hidden
              >
                01
              </span>
            </div>
          </Reveal>
        </div>

        {/* RIGHT — Step-by-step process */}
        <div className="lg:col-span-6 lg:col-start-7 mt-16 lg:mt-4">
          <div className="label-mini">How it works</div>
          <h3 className="font-serif mt-5 text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[#0A192F] max-w-xl">
            Four steps. <span className="italic text-[#C5A059]">One visit.</span>
          </h3>

          <ol className="mt-10 md:mt-12 relative">
            {/* Timeline rail */}
            <span
              className="hidden md:block absolute left-[27px] top-4 bottom-4 w-px bg-[#0A192F]/10"
              aria-hidden
            />

            {steps.map((s, i) => (
              <Reveal key={s.t} delay={0.1 + i * 0.07}>
                <li className="relative flex gap-5 md:gap-7 py-6 md:py-7 border-b border-[#0A192F]/10 last:border-none group">
                  {/* Step circle */}
                  <span
                    className="relative w-14 h-14 md:w-[54px] md:h-[54px] shrink-0 rounded-full flex items-center justify-center bg-white border border-[#0A192F]/12 text-[#C5A059] transition-colors duration-500 group-hover:border-[#C5A059] group-hover:bg-[#0A192F]"
                  >
                    <ServiceIcon name={s.icon} className="w-5 h-5" />
                  </span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3">
                      <span className="text-[#C5A059] font-mono-tab text-xs">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h4 className="font-serif text-2xl md:text-[26px] text-[#0A192F] leading-tight">
                        {s.t}
                      </h4>
                    </div>
                    <p className="mt-2 text-[#0A192F]/65 text-sm md:text-base leading-relaxed max-w-md">
                      {s.d}
                    </p>
                  </div>

                  <ArrowUpRight className="hidden md:block w-4 h-4 text-[#0A192F]/30 mt-3 shrink-0 group-hover:text-[#C5A059] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-500" />
                </li>
              </Reveal>
            ))}
          </ol>

          {/* Micro trust row */}
          <Reveal delay={0.5}>
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-[#0A192F]/10 pt-8">
              <MiniTrust icon="Timer" label="Same-day service" />
              <MiniTrust icon="Languages" label="EN · ES · PT · AR" />
              <MiniTrust icon="ParkingCircle" label="Free parking" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function MiniTrust({ icon, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-full border border-[#0A192F]/15 text-[#C5A059] flex items-center justify-center shrink-0">
        <ServiceIcon name={icon} className="w-4 h-4" />
      </span>
      <span className="text-[#0A192F]/80 text-xs md:text-[13px] leading-tight">
        {label}
      </span>
    </div>
  );
}

function FaqCard({ f, i }) {
  const [open, setOpen] = useState(false);
  const icons = ["Calendar", "Timer", "ShieldCheck", "CreditCard", "Languages"];
  return (
    <motion.button
      type="button"
      onClick={() => setOpen((v) => !v)}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
      aria-expanded={open}
      data-testid={`faq-trigger-${i}`}
      className="group relative text-left w-full h-full bg-white border border-[#0A192F]/10 hover:border-[#C5A059]/50 hover:shadow-xl hover:shadow-[#0A192F]/10 p-7 md:p-8 overflow-hidden transition-shadow duration-500"
    >
      {/* Gold corner accent */}
      <span
        className={`pointer-events-none absolute top-0 right-0 w-14 h-14 border-t border-r transition-colors duration-500 ${
          open ? "border-[#C5A059]" : "border-[#C5A059]/0 group-hover:border-[#C5A059]"
        }`}
        aria-hidden
      />

      <div className="flex items-start gap-5">
        <span className="w-12 h-12 shrink-0 rounded-full bg-[#0A192F] text-[#C5A059] flex items-center justify-center group-hover:bg-[#C5A059] group-hover:text-[#0A192F] transition-colors duration-500">
          <ServiceIcon name={icons[i % icons.length]} className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">
            <span className="w-6 h-px bg-[#C5A059]" /> FAQ {String(i + 1).padStart(2, "0")}
          </div>
          <h3 className="mt-3 font-serif text-xl md:text-[22px] text-[#0A192F] leading-tight">
            {f.q}
          </h3>
          <motion.div
            initial={false}
            animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <p className="mt-4 text-[#0A192F]/70 text-sm md:text-base leading-relaxed">
              {f.a}
            </p>
          </motion.div>

          <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0A192F]/50 group-hover:text-[#C5A059] transition-colors duration-500">
            {open ? "Close answer" : "Read answer"}
            <Plus
              className={`w-3.5 h-3.5 transition-transform duration-500 ${open ? "rotate-45" : ""}`}
            />
          </div>
        </div>
      </div>
    </motion.button>
  );
}

